/**
 * VENUE_PACK wizard — admin picks stadiums from the SYSTEM catalog.
 * Backend copies each pick into a PackVenue template row; on purchase
 * the existing _clone_pack_contents path clones into the buyer's tenant.
 *
 * Opens when /admin/marketplace mounts with ?action=new&type=VENUE_PACK.
 *
 * 2026-07-18a — CSV upload → catalog picker.
 * 2026-07-18b — full-screen layout via WizardShell (sidebar + main).
 * 2026-07-31  — Publish moved to TOURNAMENT_BUNDLE. This wizard now
 *               only saves drafts; the founder wraps the draft in a
 *               bundle from the Tournament bundles tab and publishes
 *               that instead. Old result / testMode UI removed.
 */
import { useState } from "react";
import { CheckCircle2, MapPin, Save } from "lucide-react";
import {
  createPack,
  updatePack,
  listCatalogVenues,
  selectVenuesFromCatalog,
} from "../../api/owner.api";
import CatalogPicker from "../../components/marketplace/CatalogPicker";
import PackDetailsForm from "../../components/marketplace/PackDetailsForm";
import WizardShell from "../../components/marketplace/WizardShell";

export default function VenuePackWizard({ onClose, onCreated }) {
  const [details, setDetails] = useState({
    name: "",
    season: "",
    short_description: "",
    description: "",
    price_credits: 49,
  });
  const [venueIds, setVenueIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [packId, setPackId] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  function detailsValid() {
    return details.name.trim().length >= 3
      && Number.isFinite(Number(details.price_credits))
      && Number(details.price_credits) >= 0;
  }

  function _packBody() {
    return {
      name: details.name.trim(),
      season: details.season.trim(),
      short_description: details.short_description.trim(),
      description: details.description.trim(),
      price_credits: Number(details.price_credits),
    };
  }

  async function _persist() {
    let pack;
    if (packId) {
      const { data } = await updatePack(packId, _packBody());
      pack = data;
    } else {
      const slug = details.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")
        .slice(0, 80);
      const { data } = await createPack({
        ..._packBody(),
        slug,
        pack_type: "VENUE_PACK",
      });
      pack = data;
      setPackId(pack.id);
    }
    const { data: summary } = await selectVenuesFromCatalog(pack.id, venueIds);
    return { pack, summary };
  }

  function _errorMessage(e) {
    return e?.response?.data
      ? (typeof e.response.data === "string"
          ? e.response.data
          : (e.response.data.detail
              || e.response.data.error
              || JSON.stringify(e.response.data)))
      : e.message;
  }

  function _validate() {
    if (!detailsValid()) {
      setError("Fill in name (≥ 3 chars) and a non-negative price first.");
      return false;
    }
    if (venueIds.length === 0) {
      setError("Pick at least one stadium from the catalog.");
      return false;
    }
    return true;
  }

  async function handleSave() {
    if (!_validate()) return;
    setBusy(true);
    setError(null);
    try {
      const { pack } = await _persist();
      setSavedAt(new Date());
      if (onCreated) onCreated(pack);
    } catch (e) {
      setError(_errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const sidebar = (
    <>
      <PackDetailsForm
        details={details}
        setDetails={setDetails}
        namePlaceholder="India — International venues 2026"
        shortDescriptionPlaceholder="8 international venues in India"
      />
      <div className="pt-3 border-t border-border-default text-xs text-text-secondary">
        <div className="flex items-center gap-1.5 text-text-primary font-medium mb-1">
          <MapPin className="w-3.5 h-3.5" /> {venueIds.length} venue{venueIds.length === 1 ? "" : "s"} picked
        </div>
        Pick from the SYSTEM stadium catalog on the right.
      </div>

      {savedAt && (
        <div className="pt-3 border-t border-border-default text-xs text-emerald-300 flex items-center gap-1.5">
          <CheckCircle2 className="w-3.5 h-3.5" />
          Draft saved · {savedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </div>
      )}
    </>
  );

  const isSaved = Boolean(packId);
  return (
    <WizardShell
      eyebrow="Marketplace · VENUE_PACK"
      title="New venues pack"
      onClose={onClose}
      busy={busy}
      error={error}
      sidebar={sidebar}
      primaryLabel={isSaved ? "Update draft" : "Save draft"}
      primaryIcon={<Save className="w-4 h-4" />}
      primaryDisabled={!detailsValid() || venueIds.length === 0}
      primaryTone="teal"
      onPrimary={handleSave}
    >
      <section className="space-y-3">
        <div>
          <h3 className="text-base font-semibold">Pick venues from the catalog</h3>
          <p className="text-sm text-text-secondary mt-0.5">
            Only SYSTEM-owned stadiums the platform has already ingested are
            shown. Search by name, city, or state.
          </p>
        </div>
        <CatalogPicker
          fetchFn={(params) => listCatalogVenues(params)}
          selectedIds={venueIds}
          onChange={setVenueIds}
          placeholder="Search stadiums by name, city, state…"
          emptyHint="No stadiums match. Try a different search."
          renderRow={(s) => (
            <div className="flex items-baseline justify-between gap-2">
              <div className="min-w-0">
                <span className="font-medium">{s.name}</span>
                {(s.city || s.state) && (
                  <span className="ml-2 text-xs text-text-secondary">
                    {[s.city, s.state].filter(Boolean).join(", ")}
                  </span>
                )}
              </div>
              {s.nationality_name && (
                <span className="text-[10px] uppercase tracking-wider text-text-secondary shrink-0">
                  {s.nationality_name}
                </span>
              )}
            </div>
          )}
          renderChip={(s) => (
            <span>{s.name}{s.city ? ` · ${s.city}` : ""}</span>
          )}
        />
      </section>
    </WizardShell>
  );
}
